/**
 * Contract between the browser game and the Worker. Imported by both sides
 * so request/response shapes can't drift.
 */

import { isLocale, type Locale } from "./locale";

export const SAMPLE_ENDPOINT = "/api/sample";
/** GET: is there a valid session (+ the Turnstile site key). POST: trade a Turnstile token for one. */
export const SESSION_ENDPOINT = "/api/session";
/** Turnstile action bound to the session widget; the Worker rejects tokens minted for anything else. */
export const TURNSTILE_ACTION = "start";

export const MODEL_TIERS = ["quick", "default"] as const;
export type ModelTier = (typeof MODEL_TIERS)[number];

/** Upper bound on prompt size; the debrief prompt (full play log) is the largest caller. */
export const MAX_PROMPT_CHARS = 80_000;
/** Upper bound on each analytics metadata string. */
export const MAX_META_CHARS = 2_000;

export const CALL_KINDS = ["chat", "debrief", "other"] as const;
export type CallKind = (typeof CALL_KINDS)[number];

/**
 * What the call was for, logged next to it for usage and abuse analysis.
 * Self-reported by the client, so it is a label, never an authorisation.
 */
export interface CallMeta {
  kind: CallKind;
  /** Level id, e.g. "opd". */
  level?: string;
  /** Dialog channel, e.g. "pt". */
  ch?: string;
  /** What the clerk typed (chat only). */
  said?: string;
}

export interface SampleRequest {
  prompt: string;
  tier: ModelTier;
  meta: CallMeta;
  /** Edition the player is in; picks the system prompt's language. Older clients omit it (zh-Hant). */
  locale: Locale;
}

export type SampleErrorCode =
  | "not_configured"
  | "no_session"
  | "rate_limited"
  | "quota_exhausted"
  | "bad_request"
  | "upstream_error"
  | "invalid_json";

export type SampleResponse = { ok: true; data: unknown } | { ok: false; code: SampleErrorCode; message?: string };

export interface SessionStatus {
  /** The request carried a valid session cookie. */
  active: boolean;
  /** Turnstile site key the client should render when not active. */
  siteKey: string;
}

export type SessionErrorCode = "not_configured" | "bad_request" | "turnstile_failed";
export type SessionResponse = { ok: true; expiresAt: number } | { ok: false; code: SessionErrorCode };

export function isModelTier(x: unknown): x is ModelTier {
  return typeof x === "string" && (MODEL_TIERS as readonly string[]).includes(x);
}

const optStr = (x: unknown): string | undefined | null =>
  x === undefined ? undefined : typeof x === "string" && x.length <= MAX_META_CHARS ? x : null;

/** Missing meta is accepted as `{ kind: "other" }` so older clients keep working. */
export function parseCallMeta(x: unknown): CallMeta | null {
  if (x === undefined) return { kind: "other" };
  if (typeof x !== "object" || x === null) return null;
  const o = x as Record<string, unknown>;
  const kind = o.kind;
  if (typeof kind !== "string" || !(CALL_KINDS as readonly string[]).includes(kind)) return null;
  const level = optStr(o.level);
  const ch = optStr(o.ch);
  const said = optStr(o.said);
  if (level === null || ch === null || said === null) return null;
  const meta: CallMeta = { kind: kind as CallKind };
  if (level !== undefined) meta.level = level;
  if (ch !== undefined) meta.ch = ch;
  if (said !== undefined) meta.said = said;
  return meta;
}

export function parseSampleRequest(x: unknown): SampleRequest | null {
  if (typeof x !== "object" || x === null) return null;
  const { prompt, tier, meta, locale } = x as Record<string, unknown>;
  if (typeof prompt !== "string" || prompt.length === 0 || prompt.length > MAX_PROMPT_CHARS) return null;
  if (!isModelTier(tier)) return null;
  const m = parseCallMeta(meta);
  if (!m) return null;
  if (locale !== undefined && !isLocale(locale)) return null;
  return { prompt, tier, meta: m, locale: locale ?? "zh-Hant" };
}
